import { useCallback, useEffect, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import ThoughtRecordList from "./ThoughtRecordList";
import useGetThoughtRecordData from "hooks/useGetThoughtRecordData";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
  thoughtRecordApi,
  ThoughtRecordType,
} from "api/thoughtRecordApi";
import { useRecoilState, useRecoilValue } from 'recoil'
import { patientAtom } from "recoil/patientAtom";
import { useQuery } from "@tanstack/react-query";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import useOpenNewThoughtRecord from "hooks/useOpenNewThoughtRecord";
import useIsInappWebview from "hooks/useIsInappWebview";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectedThoughtRecordKeyAtom } from 'recoil/selectedThoughtRecordAtom'

export default function ThoughtRecord({ taskKey }: { taskKey: string }) {
  const [searchParams] = useSearchParams();
  const currentThoughtRecordKeyParam = searchParams.get("trk"); // 앱에서만

  const patientState = useRecoilValue(patientAtom);
  const user = useRecoilValue(userAtom);
  const isCoach = user?.accessToken && user?.role !== "patient";
  const navigate = useNavigate();

  const isTaskContainerSmall = useIsInappWebview();
  const [data, setData] = useState<ProgramContentType[] | undefined>();

  const [thoughtRecordList, setThoughtRecordList] = useState<
    ThoughtRecordType[] | undefined
  >();

  const [currentThoughtRecord, setCurrentThoughtRecord] = useState<
    ThoughtRecordType | undefined
  >();

  const [selectedThoughtRecordKey, setSelectedThoughtRecordKey] =
    useRecoilState(selectedThoughtRecordKeyAtom);

  const [isEmpty, setIsEmpty] = useState<boolean | undefined>(undefined);

  const openNewThoughtRecord = useOpenNewThoughtRecord();

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useGetThoughtRecordData({
    trk: currentThoughtRecord?.thoughtRecordKey,
    isFirst: isEmpty,
    enabled: !!thoughtRecordList,
  });

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  const patientId = patientState?.patientId; // 원시값으로 추출

  const getThoughtRecordListqueryFn = useCallback(
    () => thoughtRecordApi.getList(`${patientId}`),
    [patientId]
  );

  // 1. param이 있을 때 처리하는 useEffect

  useEffect(() => {
    if (!thoughtRecordList || !patientId) return;

    if (currentThoughtRecordKeyParam) {
      const selectedRecord = thoughtRecordList.find(
        (element) => element.thoughtRecordKey === currentThoughtRecordKeyParam
      );

      if (selectedRecord) {
        setCurrentThoughtRecord(selectedRecord);
      } else {
        if (patientId && currentThoughtRecordKeyParam.includes(patientId)) {
          const newThoughtRecord = {
            thoughtRecordKey: currentThoughtRecordKeyParam,
            date: new Date().toISOString(),
            situation: "",
            isHighlight: false,
            hasAiRecommendation: false,
            isDone: false,
            lastDoneAt: undefined,
            sentiment: "negative",
            shouldBeDone: false,
          } as ThoughtRecordType;

          setThoughtRecordList((prevList) => [
            newThoughtRecord,
            ...(prevList || []),
          ]);
          setCurrentThoughtRecord(newThoughtRecord);
        }
      }

      navigate(`${window.location.pathname}`, { replace: true });
    }
  }, [thoughtRecordList, currentThoughtRecordKeyParam, patientId, navigate]);

  // 2. param이 없을 때 첫 번째 기록을 선택하는 useEffect
  useEffect(() => {
    if (!thoughtRecordList || currentThoughtRecordKeyParam || isFetching) return;

    if (thoughtRecordList.length > 0 && !currentThoughtRecord) {
      setCurrentThoughtRecord(thoughtRecordList[0]);
    }
  }, [thoughtRecordList, currentThoughtRecordKeyParam, currentThoughtRecord, isFetching]);

  useEffect(() => {
    if (currentThoughtRecord?.thoughtRecordKey) {
      setSelectedThoughtRecordKey(currentThoughtRecord.thoughtRecordKey!!);
    }
  }, [currentThoughtRecord]);

  useEffect(() => {
    return () => {
      setSelectedThoughtRecordKey(undefined);
    };
  }, []);

  useQuery(
    [THOUGHTRECORD_DOMAIN, THOUGHTRECORD_LIST_ENDPOINT],
    getThoughtRecordListqueryFn,
    {
      enabled: !!patientState?.patientId,
      onSuccess: (data) => {
        const list = data.result?.map((each) => ({
          ...each,
          shouldBeDone: each?.isDone
            ? false
            : thoughtRecordList?.find(
                (element) => element.thoughtRecordKey === each.thoughtRecordKey
              )?.shouldBeDone,
        }));

        setThoughtRecordList(list);

        if (!list || list.length === 0) {
          setIsEmpty(true);
          if (!currentThoughtRecord) {
            openNewThoughtRecord();
          }
        } else {
          list.sort(
            (a, b) => +new Date(b.date || "") - +new Date(a.date || "")
          );
          setIsEmpty(false);

          if (currentThoughtRecordKeyParam) {
            const selectedIndex = list.findIndex(
              (element) =>
                element.thoughtRecordKey === currentThoughtRecordKeyParam
            );
            if (selectedIndex > -1) {
              setCurrentThoughtRecord(list[selectedIndex]);
            }
          }
        }
      },
    }
  );

  useEffect(() => {
    if (fetchedData) {
      if (!_.isEqual(fetchedData.content, data)) {
        setData(fetchedData.content);
      }
    }
  }, [fetchedData]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  const handleUndoneSuccess = () => {
    setCurrentThoughtRecord((prev) =>
      prev ? { ...prev, isDone: false } : prev
    );
    setThoughtRecordList((prev) =>
      prev?.map((item) =>
        item.thoughtRecordKey === currentThoughtRecord?.thoughtRecordKey
          ? { ...item, isDone: false }
          : item
      )
    );
  };

  return (
    <>
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          taskKey={taskKey}
          thoughtRecordKey={currentThoughtRecord?.thoughtRecordKey}
          title={`생각기록지`}
          contentData={data}
          setContentData={setData}
          setMoveToIndex={setMoveToIndex}
          isDone={currentThoughtRecord?.isDone}
          lastDoneAt={currentThoughtRecord?.lastDoneAt}
          onUndoneSuccess={handleUndoneSuccess}
          {...(isTaskContainerSmall && {
            extraContent: (
              <ThoughtRecordList
                thoughtRecordList={thoughtRecordList}
                currentThoughtRecord={currentThoughtRecord}
                setCurrentThoughtRecord={setCurrentThoughtRecord}
              />
            ),
          })}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        {!isTaskContainerSmall && (
          <ThoughtRecordList
            thoughtRecordList={thoughtRecordList}
            currentThoughtRecord={currentThoughtRecord}
            setCurrentThoughtRecord={setCurrentThoughtRecord}
          />
        )}

        {currentThoughtRecord?.thoughtRecordKey && (
          <Stack sx={{ flex: 1, overflow: "hidden" }}>
            <TaskContent
              taskKey={taskKey}
              thoughtRecordKey={currentThoughtRecord?.thoughtRecordKey}
              data={data}
              setData={setData}
              moveToIndex={moveToIndex}
              setMoveToIndex={setMoveToIndex}
              isDone={currentThoughtRecord?.isDone}
              translationVersion={fetchedData?.translationVersion}
              onUndoneSuccess={handleUndoneSuccess}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
}